import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FiDownload, FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FaClipboardList, FaTools, FaHeadset } from "react-icons/fa";
import { animatePage } from "../../../animation/animationPage";
import {
  getAllAdminRequests,
  downloadAdminRequestPdf,
} from "../../../_services/adminRequests.service";
import AdminRequestsStats from "./AdminRequestsStats";
import AdminRequestDrawer from "./AdminRequestDrawer";
import styles from "./AdminRequestsPage.module.scss";

// Icone associee a chaque type de demande.
const TYPE_ICONS = {
  calibration: <FaClipboardList />,
  sav: <FaTools />,
  support: <FaHeadset />,
};

const PAGE_SIZE = 25;

const AdminRequestsPage = () => {
  const { t } = useTranslation();

  // Vue active : "list" | "stats".
  const [view, setView] = useState("list");

  // Filtres appliques (envoyes au serveur).
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pdfStatus, setPdfStatus] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingKey, setDownloadingKey] = useState(null);

  // Demande selectionnee pour le panneau de detail.
  const [selected, setSelected] = useState(null);

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const { data, pagination: pg } = await getAllAdminRequests(
          { page, pageSize: PAGE_SIZE, type, search, dateFrom, dateTo, pdfStatus },
          signal
        );
        setRows(data);
        setPagination(pg);
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError(t("admin_requests.load_error"));
      } finally {
        setLoading(false);
      }
    },
    [page, type, search, dateFrom, dateTo, pdfStatus, t]
  );

  // Recharge a chaque changement de filtre (avec petit debounce sur la recherche).
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => load(controller.signal), 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  // Remet a la page 1 quand un filtre change (hors page).
  useEffect(() => {
    setPage(1);
  }, [type, search, dateFrom, dateTo, pdfStatus]);

  const handleDownload = async (row) => {
    const key = `${row.type}-${row.id}`;
    setDownloadingKey(key);
    try {
      await downloadAdminRequestPdf(row.type, row.id, row.pdf_filename);
    } catch (err) {
      setError(t("admin_requests.download_error"));
    } finally {
      setDownloadingKey(null);
    }
  };


  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
  };

  return (
    <motion.div
      className={styles.page}
      variants={animatePage}
      exit="exit"
      initial="hidden"
      animate="visible"
    >
      <header className={styles.header}>
        <h1 className={styles.title}>{t("admin_requests.title")}</h1>
        <p className={styles.subtitle}>{t("admin_requests.subtitle")}</p>
      </header>

      {/* Bascule Liste / Statistiques */}
      <div className={styles.viewToggle}>
        <button
          type="button"
          className={`${styles.viewBtn} ${view === "list" ? styles.viewActive : ""}`}
          onClick={() => setView("list")}
        >
          {t("admin_requests.view_list")}
        </button>
        <button
          type="button"
          className={`${styles.viewBtn} ${view === "stats" ? styles.viewActive : ""}`}
          onClick={() => setView("stats")}
        >
          {t("admin_requests.view_stats")}
        </button>
      </div>

      {view === "stats" ? (
        <AdminRequestsStats />
      ) : (
        <>
      {/* Barre de filtres */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin_requests.search_placeholder")}
            className={styles.searchInput}
          />
        </div>

        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={styles.select}
        >
          <option value="">{t("admin_requests.filter_all_types")}</option>
          <option value="calibration">{t("admin_requests.type_calibration")}</option>
          <option value="sav">{t("admin_requests.type_sav")}</option>
          <option value="support">{t("admin_requests.type_support")}</option>
        </select>

        <select
          value={pdfStatus}
          onChange={(e) => setPdfStatus(e.target.value)}
          className={styles.select}
        >
          <option value="">{t("admin_requests.filter_all_status")}</option>
          <option value="available">{t("admin_requests.status_available")}</option>
          <option value="archived">{t("admin_requests.status_archived")}</option>
        </select>

        <label className={styles.dateLabel}>
          {t("admin_requests.date_from")}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={styles.dateInput}
          />
        </label>

        <label className={styles.dateLabel}>
          {t("admin_requests.date_to")}
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={styles.dateInput}
          />
        </label>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Tableau */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("admin_requests.col_type")}</th>
              <th>{t("admin_requests.col_ref")}</th>
              <th>{t("admin_requests.col_user")}</th>
              <th>{t("admin_requests.col_summary")}</th>
              <th>{t("admin_requests.col_cabine")}</th>
              <th>{t("admin_requests.col_date")}</th>
              <th>{t("admin_requests.col_status")}</th>
              <th>{t("admin_requests.col_action")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className={styles.empty}>
                  {t("admin_requests.loading")}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.empty}>
                  {t("admin_requests.empty")}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = `${row.type}-${row.id}`;
                return (
                  <tr
                    key={key}
                    className={styles.clickableRow}
                    onClick={() => setSelected({ type: row.type, id: row.id })}
                  >
                    <td>
                      <span className={styles.typeCell}>
                        {TYPE_ICONS[row.type]}
                        {t(`admin_requests.type_${row.type}`)}
                      </span>
                    </td>
                    <td className={styles.mono}>{row.ref_number || "-"}</td>
                    <td>
                      <div className={styles.userName}>
                        {row.user_display_name || row.name || "-"}
                      </div>
                      <div className={styles.userEmail}>
                        {row.user_account_email || row.email || ""}
                      </div>
                    </td>
                    <td>{row.summary || "-"}</td>
                    <td>{row.cabine_name || "-"}</td>
                    <td>{formatDate(row.created_at)}</td>
                    <td>
                      {row.pdf_available ? (
                        <span className={styles.badgeOk}>
                          {t("admin_requests.status_available")}
                        </span>
                      ) : (
                        <span className={styles.badgeArchived}>
                          {t("admin_requests.status_archived")}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.downloadBtn}
                        disabled={!row.pdf_available || downloadingKey === key}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(row);
                        }}
                        title={
                          row.pdf_available
                            ? t("admin_requests.download")
                            : t("admin_requests.status_archived")
                        }
                      >
                        <FiDownload />
                        {downloadingKey === key
                          ? t("admin_requests.downloading")
                          : t("admin_requests.download")}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={styles.pagination}>
        <span className={styles.paginationInfo}>
          {t("admin_requests.total_count", { count: pagination.total })}
        </span>
        <div className={styles.paginationControls}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <FiChevronLeft />
          </button>
          <span className={styles.pageIndicator}>
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page >= pagination.totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            <FiChevronRight />
          </button>
        </div>
      </div>
        </>
      )}

      <AdminRequestDrawer
        open={!!selected}
        type={selected?.type}
        requestId={selected?.id}
        onClose={() => setSelected(null)}
        onDownload={(detail) =>
          handleDownload({
            type: detail.type,
            id: detail.id,
            pdf_filename: detail.pdf_filename,
          })
        }
      />
    </motion.div>
  );
};

export default AdminRequestsPage;
